<template>
  <div v-if="items.length" class="ce-cfg">
    <span class="ce-cfg__lead">{{ lead }}</span>
    <span v-for="item in items" :key="item.label" class="ce-cfg__item">
      <span class="ce-cfg__label">{{ item.label }}</span>
      <span class="ce-cfg__value">{{ item.value }}</span>
    </span>
    <router-link class="ce-cfg__link" to="/change-events/settings">Change these</router-link>
  </div>
</template>

<script>
  import { floodRuleText } from '../consts/ChangeEvents.consts';

  /**
   * The settings that produced the rows below it.
   *
   * A reader cannot judge a list of site incidents without knowing the rule
   * that opened them, and cannot judge a list of groups without knowing the
   * window they were grouped in. So each list states its own rule.
   *
   * Only the settings that apply to THAT list are shown. The grouping window
   * has nothing to do with whether an incident opens, and showing it there
   * would invite the same mix-up we have spent this project removing.
   */
  export default {
    name: 'ConfigBanner',
    props: {
      /** Which list this sits above: `incidents` or `grouping`. */
      mode: {
        type: String,
        required: true
      },
      /** The config object from `GET /change_events/config`, or null. */
      config: {
        type: Object,
        default: null
      }
    },
    computed: {
      lead() {
        return this.mode === 'incidents'
          ? 'Site-Wide Incidents Configuration:'
          : 'Grouped Alerts Configuration:';
      },
      items() {
        const c = this.config;
        if (!c) return [];

        if (this.mode === 'incidents') {
          return [
            {
              label: 'Window opening criteria',
              // One sentence, shared with the settings help, so the two
              // screens an administrator compares cannot disagree.
              value: floodRuleText(c.flood_threshold)
            },
            {
              label: 'Alerts received within',
              value: this.duration(c.flood_window_minutes)
            },
            {
              label: 'Auto close window after inactivity (no alert)',
              value: this.duration(c.auto_resolve_minutes)
            }
          ];
        }
        return [
          {
            label: 'Group closes after inactivity (no alert)',
            value: this.duration(c.group_window_minutes)
          },
          {
            label: 'Minimum alert similarity',
            value: this.pct(c.similarity_threshold)
          }
        ];
      }
    },
    methods: {
      num(v) {
        const n = Number(v);
        return Number.isFinite(n) ? n.toLocaleString('en-US') : '-';
      },
      /** Minutes as the shortest true form: 90 minutes, 4 hours, 2 days. */
      duration(minutes) {
        const m = Number(minutes);
        if (!Number.isFinite(m) || m <= 0) return '-';
        if (m % 1440 === 0) return this.plural(m / 1440, 'day');
        if (m % 60 === 0) return this.plural(m / 60, 'hour');
        return this.plural(m, 'minute');
      },
      plural(n, word) {
        return `${n} ${word}${n === 1 ? '' : 's'}`;
      },
      pct(v) {
        const n = Number(v);
        return Number.isFinite(n) ? `${Math.round(n * 100)}%` : '-';
      }
    }
  };
</script>

<style lang="scss" scoped>
  .ce-cfg {
    display: flex;
    flex-wrap: wrap;
    align-items: baseline;
    gap: 6px 18px;
    padding: 8px 16px;
    margin: 0 0 4px;
    background: #fafafa;
    border: 1px solid #f0f2f4;
    border-radius: 4px;
    font-size: 12px;
    line-height: 1.5;

    &__lead {
      font-weight: 600;
      color: #595959;
    }
    &__item {
      white-space: nowrap;
    }
    &__label {
      color: #8c8c8c;
      margin-right: 5px;
    }
    &__value {
      font-weight: 600;
      color: #262626;
    }
    &__link {
      margin-left: auto;
      white-space: nowrap;
    }
  }
</style>
